import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import SimpleModal from 'lib/modal/simple-modal';
import { ModalProps } from 'approot/shared/modal/modal.constants';
import { Heading16 } from 'approot/shared/styles/typography.style';
import {
  SimpleModalContentStyled,
  SimpleModalVerticalActionsStyled,
} from 'lib/modal/simple-modal.styles';
import { TextInputAreaStyled } from 'approot/shared/styles/inputs.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import ContactSupportWithDisclaimer from 'approot/shared/contact-support/contact-support-with-disclaimer';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import { CREATE_FEEDBACK_SURVEY } from './feedback-survey.graphql';
import type {
  CreateFeedbackSurveyMutation,
  CreateFeedbackSurveyMutationVariables,
} from './feedback-survey.graphql.generated';
import ValidationMessage from 'approot/shared/validation/validation-message';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { FeedbackFeature } from 'global/constants';
import Text from 'approot/shared/styles/typography/text';

interface FeedbackSurveyModalProps extends ModalProps {
  feature: FeedbackFeature;
}

const FEEDBACK_FIELD = 'feedback';

type FormData = {
  [FEEDBACK_FIELD]: string;
};

const FeedbackSurveyModal = ({
  isVisible,
  closeModal,
  feature,
}: FeedbackSurveyModalProps) => {
  const { showSuccess, showError } = useSnackbar();
  const formMethods = useForm<FormData>({
    defaultValues: {
      [FEEDBACK_FIELD]: '',
    },
  });
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = formMethods;

  const [createFeedbackSurvey, { loading }] = useLoggingMutation<
    CreateFeedbackSurveyMutation,
    CreateFeedbackSurveyMutationVariables
  >(CREATE_FEEDBACK_SURVEY, {
    onCompleted: () => {
      showSuccess('Thank you for your feedback :)');
      reset();
      closeModal();
    },
    onError: () => {
      showError('We could not submit your feedback. Please try again.');
    },
  });

  const onSubmit = useCallback(
    ({ feedback }: FormData) => {
      createFeedbackSurvey({
        variables: {
          input: {
            feature,
            feedbackText: feedback,
            url: window.location.href,
            data: {
              userAgent: navigator.userAgent,
              resolution: `${window.screen.width}x${window.screen.height}`,
            },
          },
        },
      });
    },
    [createFeedbackSurvey, feature]
  );

  return (
    <SimpleModal
      headerTitle="Give us Feedback"
      title="Give us Feedback"
      isVisible={isVisible}
      onClose={closeModal}
      size={500}
    >
      <SimpleModalContentStyled>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Heading16 as="h2">
              What feedback would you like to share?
            </Heading16>
            <Text $marginBottom={4}>
              Please describe any suggestions, issues, or improvements you’d
              like to see
            </Text>
            <Box $flexDirection="column" $marginBottom={0}>
              <TextInputAreaStyled
                {...register(FEEDBACK_FIELD, {
                  required: 'Feedback is required',
                })}
                id={FEEDBACK_FIELD}
                name={FEEDBACK_FIELD}
                rows={5}
                maxLength={1000}
              />
              <ValidationMessage errors={errors} field={FEEDBACK_FIELD} />
            </Box>
            <SimpleModalVerticalActionsStyled>
              <PrimaryButton type="submit" loading={loading} disabled={loading}>
                Submit
              </PrimaryButton>
            </SimpleModalVerticalActionsStyled>
            <Box $marginTop={12} $justifyContent="flex-start">
              <ContactSupportWithDisclaimer
                disclaimerText="We don’t reply to feedback left here, but we do read it all to make Inquisitive better for teachers. If you’d like to talk to us or need help, "
                linkText="click here to chat with us"
              />
            </Box>
          </form>
        </FormProvider>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export default FeedbackSurveyModal;
